import React, { useState } from 'react';
import { ASSOCIATION_TYPE_LABELS } from '../../constants/associations';

export default function AssociationsSection({
  associations = [],
  canEdit = false,
  onRemoveRequest
}) {
  const byType = (associations || []).reduce((acc, row) => {
    const t = (row.association_type || row.relationship_type || '').toLowerCase();
    if (!acc[t]) acc[t] = [];
    acc[t].push(row);
    return acc;
  }, {});

  const types = Object.keys(byType).sort();

  if (!types.length) {
    return <p style={styles.emptyText}>No associations yet.</p>;
  }

  return (
    <div>
      {types.map((type) => (
        <div key={type} style={{ marginBottom: 12 }}>
          <div style={styles.typeHeading}>{ASSOCIATION_TYPE_LABELS[type] || type}</div>
          {(byType[type] || []).map((row) => (
            <div key={row.id} style={styles.row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.rowTitle}>
                  {row.groups_periods_label || row.group_name || 'Group-period'}
                </div>
                {(row.period_title || row.start_date || row.end_date) && (
                  <div style={styles.rowMeta}>
                    {row.period_title ? <span>{row.period_title}</span> : null}
                    {row.start_date ? <span>From {row.start_date}</span> : null}
                    {row.end_date ? <span>Until {row.end_date}</span> : null}
                  </div>
                )}
              </div>
              {canEdit && onRemoveRequest ? (
                <button
                  type="button"
                  style={styles.removeBtn}
                  onClick={() => onRemoveRequest(row)}
                  title="Remove association"
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function AddAssociationModal({
  isOpen,
  onClose,
  ownerUgpId,
  enabledTypes = [],
  assignmentMetadata = {},
  onCreated
}) {
  const [associationType, setAssociationType] = useState(enabledTypes[0]?.type || '');
  const [targetGpId, setTargetGpId] = useState('');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!isOpen) return;
    setAssociationType(enabledTypes[0]?.type || '');
    setTargetGpId('');
    setError('');
  }, [isOpen, enabledTypes]);

  React.useEffect(() => {
    if (!isOpen || !ownerUgpId || !associationType) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoadingOptions(true);
    const params = new URLSearchParams({
      user_groups_periods_id: String(ownerUgpId),
      association_type: associationType,
      assignment_metadata: JSON.stringify(assignmentMetadata || {})
    });
    fetch(`/api/associations/target-options?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setOptions(d.success ? d.data || [] : []);
        if (!d.success) setError(d.message || 'Failed to load options');
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load options');
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, ownerUgpId, associationType, assignmentMetadata]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!targetGpId) {
      setError('Select a group-period.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/associations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_groups_periods_id: ownerUgpId,
          groups_periods_id: targetGpId,
          association_type: associationType,
          assignment_metadata: assignmentMetadata || {}
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save');
      onCreated?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={() => !saving && onClose()}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.modalTitle}>Add association</h3>
        {error ? <p style={styles.error}>{error}</p> : null}
        <label style={styles.label}>Type</label>
        <select
          value={associationType}
          onChange={(e) => {
            setAssociationType(e.target.value);
            setTargetGpId('');
          }}
          style={styles.select}
          disabled={saving}
        >
          {enabledTypes.map((t) => (
            <option key={t.type} value={t.type}>
              {t.label}
            </option>
          ))}
        </select>
        <label style={styles.label}>Group-period</label>
        <select
          value={targetGpId}
          onChange={(e) => setTargetGpId(e.target.value)}
          style={styles.select}
          disabled={saving || loadingOptions}
        >
          <option value="">{loadingOptions ? 'Loading…' : 'Select group-period…'}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <div style={styles.footer}>
          <button type="button" onClick={() => !saving && onClose()} style={styles.btnNeutral} disabled={saving}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} style={styles.btnPrimary} disabled={saving}>
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  emptyText: { fontSize: '0.875rem', color: '#64748b', margin: 0 },
  typeHeading: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#475569',
    marginBottom: 6
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 8,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: 6
  },
  rowTitle: { fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' },
  rowMeta: {
    fontSize: '0.8rem',
    color: '#64748b',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0 10px',
    marginTop: 4
  },
  removeBtn: {
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: 6,
    border: '1px solid #fecaca',
    background: '#fff',
    color: '#b91c1c',
    cursor: 'pointer'
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 12000
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    padding: '20px 22px',
    width: 'min(420px, 92vw)',
    boxShadow: '0 20px 50px rgba(0,0,0,0.18)'
  },
  modalTitle: { margin: '0 0 14px', fontSize: '1.1rem', color: '#0f172a' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: 4 },
  select: {
    width: '100%',
    marginBottom: 12,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: '0.9rem'
  },
  error: { color: '#b91c1c', fontSize: '0.85rem', margin: '0 0 10px' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
  btnNeutral: {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    background: '#fff',
    cursor: 'pointer'
  },
  btnPrimary: {
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer'
  }
};
